import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";

import { Icon } from "@/components/icons/Icon";
import { joinNextChangeToUndoStep } from "@/components/tiptap/Collaboration";
import { GridMenuSheet } from "@/components/tiptap/grid-controls/GridMenuSheet";
import {
  anchorGridMenu,
  applyPendingGridAction,
  getActiveGridTarget,
  getGridLineTargets,
  moveCaretToGridTarget,
  moveGridLine,
  resolveGridMenu,
  selectGridLine,
  setPendingGridMenu,
} from "@/components/tiptap/grid-controls/gridTarget";
import { callMobileBridge, hasMobileBridgeMethod } from "@/components/tiptap/utils/useMobileBridge";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type {
  AnchoredGridMenu,
  GridAxis,
  GridMenuTarget,
  GridTarget,
} from "@/components/tiptap/grid-controls/gridTarget";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/react";
import type { MouseEvent, PointerEvent } from "react";

// Movement past which a press on a handle is a drag, not a tap.
const DRAG_THRESHOLD_PX = 6;

// The 24px column handle icon inside its `p-2` touch area.
const COL_HANDLE_HALF_WIDTH_PX = 20;

type Point = { top: number; left: number };

type Pointer = { x: number; y: number };

type HandlesLayout = { row: Point; col: Point };

/**
 * Touch handles for the grid the caret is in (table, matrix, cases): one above
 * the grid over the caret's column, one left of it beside the caret's row.
 * Tapping one opens the row/column menu: the app's native sheet inside the
 * mobile WebView, GridMenuSheet in a browser. Dragging one selects its
 * row/column and moves it, content and all, to where the pointer is.
 *
 * Portaled to `body` in document coordinates: the grids sit inside horizontal
 * scroll containers that would clip handles on their top/left edges, and inside
 * ProseMirror-owned DOM. The page itself scrolls on `window`, so absolute
 * positioning follows it without re-measuring.
 */
export const GridControlsOverlay = observer(function GridControlsOverlay({
  doc,
}: {
  doc: DocumentModel;
}) {
  const { tiptap } = doc.editor;
  const { isMobileWebView } = useStore().view;
  const [sheet, setSheet] = useState({ open: false, axis: "row" as GridAxis });
  const [dragging, setDragging] = useState<GridMenuTarget | null>(null);
  // Pointer moves can outrun re-renders.
  const draggingRef = useRef<AnchoredGridMenu | null>(null);
  const hasMovedRef = useRef(false);
  // The selected table line has no caret target, so the dragged one stands in.
  const target = dragging?.target ?? (doc.editable ? getActiveGridTarget(doc) : null);
  const targetKey = target && getTargetKey(target);

  // Table selection lives in ProseMirror state, which MobX doesn't see. Re-render
  // only when a transaction changes the target, not on every keystroke.
  const subscribeToTransactions = useCallback(
    (onChange: () => void) => {
      tiptap?.on("transaction", onChange);
      return () => {
        tiptap?.off("transaction", onChange);
      };
    },
    [tiptap],
  );
  useSyncExternalStore(
    subscribeToTransactions,
    () => getTargetShapeKey(getActiveGridTarget(doc)),
    () => null,
  );

  const layout = useHandlesLayout(tiptap, target, targetKey);

  const openMenu = (menu: GridMenuTarget) => {
    setPendingGridMenu(doc, menu);
    selectGridLine(doc, menu);

    if (isMobileWebView) {
      callMobileBridge("openGridMenu", {
        axis: menu.axis,
        // Installed app builds disable Delete without it; the last line now removes the grid.
        canRemove: true,
        canCopy: hasMobileBridgeMethod("copyToClipboard"),
      });
      return;
    }

    // Close the keyboard so it doesn't cover the sheet; the action refocuses the grid.
    tiptap?.commands.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSheet({ open: true, axis: menu.axis });
  };

  const updateDragging = (next: GridMenuTarget | null) => {
    draggingRef.current = next && anchorGridMenu(doc, next);
    setDragging(next);
  };

  const startDrag = (menu: GridMenuTarget) => {
    selectGridLine(doc, menu);
    updateDragging(menu);
    hasMovedRef.current = false;
    doc.editor.stopUndoCapturing();
  };

  // Collaborators may edit mid-drag, shifting the dragged line in the doc.
  const resolveDragging = () => {
    const anchored = draggingRef.current;
    const current = anchored && resolveGridMenu(doc, anchored);
    if (anchored && current && !isSameTarget(current.target, anchored.menu.target)) {
      updateDragging(current);
    }
    return current;
  };

  const dragTo = (pointer: Pointer) => {
    const current = resolveDragging();
    if (!current || !tiptap) return;

    const { target: dragged, axis } = current;
    const from = axis === "row" ? dragged.row : dragged.col;
    const lines = getGridLineTargets(doc, dragged, axis);
    const to = findDropIndex(tiptap, lines, axis, pointer, from);
    if (to === from) return;

    // One drag is one undo step, however many lines it crosses.
    if (hasMovedRef.current) {
      joinNextChangeToUndoStep(tiptap.state);
    }
    const moved = moveGridLine(doc, current, to);

    if (moved) {
      hasMovedRef.current = true;
      updateDragging({ target: moved, axis });
    }
  };

  const endDrag = () => {
    const current = resolveDragging();
    updateDragging(null);
    doc.editor.stopUndoCapturing();
    if (current) {
      moveCaretToGridTarget(doc, current.target);
    }
  };

  // Stays mounted while the handles hide (the editor blurs, a table line gets selected).
  const sheetElement = !isMobileWebView && (
    <GridMenuSheet
      {...sheet}
      onClose={() => setSheet((s) => ({ ...s, open: false }))}
      onAction={(action) => applyPendingGridAction(doc, action)}
    />
  );

  return (
    <>
      {target &&
        layout &&
        createPortal(
          <>
            {dragging?.axis !== "row" && (
              <GridHandle
                axis="col"
                point={layout.col}
                onOpen={() => openMenu({ target, axis: "col" })}
                onDragStart={() => startDrag({ target, axis: "col" })}
                onDrag={dragTo}
                onDragEnd={endDrag}
              />
            )}
            {dragging?.axis !== "col" && (
              <GridHandle
                axis="row"
                point={layout.row}
                onOpen={() => openMenu({ target, axis: "row" })}
                onDragStart={() => startDrag({ target, axis: "row" })}
                onDrag={dragTo}
                onDragEnd={endDrag}
              />
            )}
          </>,
          document.body,
        )}
      {sheetElement}
    </>
  );
});

function GridHandle({
  axis,
  point,
  onOpen,
  onDragStart,
  onDrag,
  onDragEnd,
}: {
  axis: GridAxis;
  point: Point;
  onOpen: () => void;
  onDragStart: () => void;
  onDrag: (pointer: Pointer) => void;
  onDragEnd: () => void;
}) {
  const t = useTranslations("gridMenu");
  const drag = useRef<{ startX: number; startY: number; moved: boolean } | null>(null);
  const draggedRef = useRef(false);

  // Keep the caret (and the math input's focus) where it is.
  const keepFocus = (e: PointerEvent | MouseEvent) => e.preventDefault();

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    keepFocus(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, moved: false };
    draggedRef.current = false;
  };

  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (!state) return;
    const distance = Math.hypot(e.clientX - state.startX, e.clientY - state.startY);
    if (!state.moved) {
      if (distance < DRAG_THRESHOLD_PX) return;
      state.moved = true;
      onDragStart();
    }
    onDrag({ x: e.clientX, y: e.clientY });
  };

  // Also runs on the `lostpointercapture` after a `pointerup`, which must not
  // undo `draggedRef` before the click it suppresses.
  const endDrag = () => {
    if (!drag.current) return;
    draggedRef.current = drag.current.moved;
    if (draggedRef.current) {
      onDragEnd();
    }
    drag.current = null;
  };

  // A collaborator deleting the grid mid-drag unmounts the handle before any
  // `pointerup`, and the drag would otherwise outlive it.
  const endDragRef = useRef(endDrag);
  endDragRef.current = endDrag;
  useEffect(() => () => endDragRef.current(), []);

  return (
    <button
      type="button"
      data-testid={`grid-${axis}-handle`}
      aria-label={t(`${axis}Handle`)}
      className={twMerge(
        "absolute z-20 flex p-2 touch-none",
        axis === "col"
          ? "-translate-x-1/2 -translate-y-full"
          : "-translate-x-full -translate-y-1/2",
      )}
      style={point}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onMouseDown={keepFocus}
      onClick={(e) => {
        // The portal still bubbles through the editor's React tree, whose click
        // handler resets the math input.
        e.stopPropagation();
        if (draggedRef.current) {
          draggedRef.current = false;
          return;
        }
        onOpen();
      }}
    >
      <Icon
        name={axis === "col" ? "GridHandleHorizontal" : "GridHandleVertical"}
        // Keeps the dots white in dark mode too.
        className="block [--color-icon-bg:white]"
      />
    </button>
  );
}

function getTargetKey(target: GridTarget) {
  return target.kind === "table" ? `table:${target.cellPos}` : `matrix:${target.cell.id}`;
}

function getTargetShapeKey(target: GridTarget | null) {
  if (!target) return null;
  const { row, col, rows, cols } = target;
  return `${getTargetKey(target)}:${row}:${col}:${rows}x${cols}`;
}

function isSameTarget(a: GridTarget, b: GridTarget) {
  return getTargetShapeKey(a) === getTargetShapeKey(b);
}

function findGridElements(editor: Editor, target: GridTarget) {
  let cell: Element | null = null;

  if (target.kind === "table") {
    const dom = editor.view.nodeDOM(target.cellPos);
    cell = dom instanceof Element ? dom : null;
  } else {
    cell = editor.view.dom.querySelector(`[data-matrix-cell-id="${CSS.escape(target.cell.id)}"]`);
  }

  const grid = cell?.closest(target.kind === "table" ? "table" : "[data-matrix-grid]");

  return cell && grid ? { cell, grid } : null;
}

/**
 * The index the dragged line at `from` should move to: past each neighbour whose
 * center the pointer crossed. Comparing against neighbours, not the line under
 * the pointer, keeps unequal rows from swapping back and forth.
 */
function findDropIndex(
  editor: Editor,
  lines: GridTarget[],
  axis: GridAxis,
  pointer: Pointer,
  from: number,
) {
  const coordinate = axis === "row" ? pointer.y : pointer.x;
  const centerOf = (index: number) => {
    const rect = findGridElements(editor, lines[index])?.cell.getBoundingClientRect();
    if (!rect) return null;
    return axis === "row" ? rect.top + rect.height / 2 : rect.left + rect.width / 2;
  };

  let index = from;
  while (index < lines.length - 1 && coordinate > (centerOf(index + 1) ?? Infinity)) {
    index += 1;
  }
  if (index !== from) {
    return index;
  }
  while (index > 0 && coordinate < (centerOf(index - 1) ?? -Infinity)) {
    index -= 1;
  }
  return index;
}

function useHandlesLayout(
  editor: Editor | undefined,
  target: GridTarget | null,
  targetKey: string | null,
) {
  const [layout, setLayout] = useState<HandlesLayout | null>(null);
  const targetRef = useRef(target);
  targetRef.current = target;
  const scheduleMeasureRef = useRef<(() => void) | null>(null);

  // The owner re-renders on cursor moves and transactions, which can shift the grid.
  useEffect(() => {
    scheduleMeasureRef.current?.();
  });

  useEffect(() => {
    if (!editor || !targetKey) {
      setLayout(null);
      return undefined;
    }

    let frame = 0;
    let observed: Element | null = null;
    const resizeObserver = new ResizeObserver(() => scheduleMeasureRef.current?.());

    const measure = () => {
      const { current } = targetRef;
      const elements = current && findGridElements(editor, current);

      if (!elements) {
        setLayout(null);
        return;
      }

      if (observed !== elements.grid) {
        if (observed) resizeObserver.unobserve(observed);
        resizeObserver.observe(elements.grid);
        observed = elements.grid;
      }

      const grid = elements.grid.getBoundingClientRect();
      const cell = elements.cell.getBoundingClientRect();
      const visible = getVisibleRange(elements.grid, editor.view.dom);
      const cellCenter = cell.left + cell.width / 2;
      // A cell scrolled out of view keeps its handle at the visible edge, all of
      // it on screen: past the edge it would widen the page.
      const colMin = Math.max(visible.left, COL_HANDLE_HALF_WIDTH_PX);
      const colMax = Math.min(
        visible.right,
        document.documentElement.clientWidth - COL_HANDLE_HALF_WIDTH_PX,
      );
      const colLeft = Math.min(Math.max(cellCenter, colMin), colMax);
      const rowLeft = Math.max(grid.left, visible.left);
      const next: HandlesLayout = {
        col: { top: grid.top + window.scrollY, left: colLeft + window.scrollX },
        row: { top: cell.top + cell.height / 2 + window.scrollY, left: rowLeft + window.scrollX },
      };

      setLayout((prev) => (prev && isSameLayout(prev, next) ? prev : next));
    };

    // After the grid's own re-render (MobX and ProseMirror update it separately).
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    scheduleMeasureRef.current = scheduleMeasure;
    scheduleMeasure();
    editor.on("update", scheduleMeasure);
    // Capture catches the horizontal scroll containers around wide tables/equations.
    window.addEventListener("scroll", scheduleMeasure, { capture: true, passive: true });
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      scheduleMeasureRef.current = null;
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      editor.off("update", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, { capture: true });
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [editor, targetKey]);

  return targetKey ? layout : null;
}

/**
 * The horizontal span of `element` that its scroll containers show: a wide
 * table or equation scrolled sideways runs past its container's edges.
 */
function getVisibleRange(element: Element, root: Element) {
  let left = -Infinity;
  let right = Infinity;

  for (
    let parent = element.parentElement;
    parent && parent !== root;
    parent = parent.parentElement
  ) {
    if (getComputedStyle(parent).overflowX !== "visible") {
      const rect = parent.getBoundingClientRect();
      left = Math.max(left, rect.left);
      right = Math.min(right, rect.right);
    }
  }

  return { left, right };
}

function isSameLayout(a: HandlesLayout, b: HandlesLayout) {
  return (
    a.col.top === b.col.top &&
    a.col.left === b.col.left &&
    a.row.top === b.row.top &&
    a.row.left === b.row.left
  );
}
